import {
  useFeatureIsOn as useGrowthbookFeatureIsOn,
  useFeatureValue,
} from '@growthbook/growthbook-react';

import { growthbook } from '../services/growthbook';
import { debugStoreSelector, useDebugStore } from '../stores/debugStore';

import { GROWTHBOOK_LOCAL_OVERRIDES } from './constants';

const growthbookLocalOverrides = GROWTHBOOK_LOCAL_OVERRIDES ?? {};

/*
  IMPORTANT NOTICE
  When cleaning up a feature flag, make sure the feature flag is not deleted from Growthbook until the cleanup code has been deployed for at least two weeks.
  This is to ensure prod is not broken incase of a rollback.
*/
const useFeatureIsOn = (key: string): boolean => {
  return useGrowthbookFeatureIsOn(key) || growthbookLocalOverrides[key];
};

export const isGrowthbookFeatureOn = (key: string): boolean => {
  return Boolean(growthbook?.isOn(key)) || !!growthbookLocalOverrides[key];
};

// exporting the shape tool key helps with re-using it for e2e tests
export const shapeToolFeatureFlagKey = 'shape-tool';
export const useShapetoolFeature = (): boolean => {
  return useFeatureIsOn(shapeToolFeatureFlagKey);
};

export const FEATURE_FLAG_RULER_AND_GUIDES = 'rulers-and-guides';
export const useRulerAndGuidesFeature = (): boolean => {
  return useFeatureIsOn(FEATURE_FLAG_RULER_AND_GUIDES);
};

export const FEATURE_FLAG_BLEED = 'bleed';
export const useBleedFeature = (): boolean => {
  return useFeatureIsOn(FEATURE_FLAG_BLEED);
};

export const FEATURE_FLAG_MARGINS = 'margins';
export const useMarginsFeature = (): boolean => {
  return useFeatureIsOn(FEATURE_FLAG_MARGINS);
};

export const FEATURE_FLAG_DOWNLOAD_REFACTORING = 'download-refactoring';
export const useDownloadRefactoringFeature = (): boolean => {
  return useFeatureIsOn(FEATURE_FLAG_DOWNLOAD_REFACTORING);
};

export const FEATURE_FLAG_RICHTEXT = 'richtext';
export const useRichTextFeature = (): boolean => {
  return useFeatureIsOn(FEATURE_FLAG_RICHTEXT);
};

export const useColorPalFeature = (): boolean => {
  return useFeatureIsOn('color-pal');
};

export const useFreeTrialFeature = (): boolean => {
  return useFeatureIsOn('free-trial');
};

export const useLocalPricingFeature = (): boolean => {
  return useFeatureIsOn('local-pricing');
};

export const useAssetsFeature = (): boolean => {
  return useFeatureIsOn('brand-kit-assets');
};

export const useCSATSurveysFeature = (): boolean => {
  return useFeatureIsOn('csat-survey');
};

export const useMediaPopoverFeature = (): boolean => {
  return useFeatureIsOn('media-popover');
};

const performanceTrackingKey = 'performance-tracking';
export const performanceTrackingIsOn = (): boolean =>
  isGrowthbookFeatureOn(performanceTrackingKey);

const customPerformanceTrackingKey = 'custom-performance-tracking';
export const customPerformanceTrackingIsOn = (): boolean =>
  isGrowthbookFeatureOn(customPerformanceTrackingKey);

const illustrationErasingKey = 'illustration-erasing';
export const useRasterizeAndEraseIsOn = (): boolean =>
  useFeatureIsOn(illustrationErasingKey);
export const rasterizeAndEraseIsOn = (): boolean =>
  isGrowthbookFeatureOn(illustrationErasingKey);

// Introduces an improved checkout experience
// To be removed by https://app.clickup.com/t/86c18rh0y
export const useRevampedCheckoutFlowVariant = (): string => {
  return useFeatureValue('checkout-flow-revamp-variant', 'off');
};

export const useHideOptimizeImageInMobile = (): boolean => {
  return useFeatureIsOn('hide-optimize-image-in-mobile');
};

export const downloadSelectionButtonIsEnabled = (): boolean =>
  isGrowthbookFeatureOn('download-selection-button');

export const useMonoTypeFontsEnabled = (): boolean =>
  useFeatureIsOn('monotype-fonts');

export const useOnboardingChecklistEnabled = (): boolean =>
  useFeatureIsOn('onboarding-checklist');

/**
 * Advanced text editing
 * https://app.clickup.com/2498318/v/b/li/901506323765
 */
export const advancedTextEditingKey = 'editor-advanced-text-editing';

export const useAdvancedTextEditing = (): boolean => {
  const featureEnabled = useFeatureIsOn(advancedTextEditingKey);
  const overrideEnabled = useDebugStore(
    debugStoreSelector.advancedTextEditingEnabled
  );

  if (overrideEnabled === undefined) {
    return featureEnabled;
  }

  return featureEnabled && overrideEnabled;
};

export const advancedTextEditingIsEnabled = (): boolean => {
  const featureEnabled = isGrowthbookFeatureOn(advancedTextEditingKey);
  const overrideEnabled = debugStoreSelector.advancedTextEditingEnabled(
    useDebugStore.getState()
  );

  if (overrideEnabled === undefined) {
    return featureEnabled;
  }

  return featureEnabled && overrideEnabled;
};

export const useEditorAndDashboardUpdateFeature = (): boolean => true;

export const copyPasteStyleIsOn = (): boolean =>
  isGrowthbookFeatureOn('editor-copy-paste-style');

export const newDefaultFontIsOn = (): boolean =>
  isGrowthbookFeatureOn('new-default-font');

const quiteModeKey = 'quite-mode';
export const useQuietModeFeature = (): boolean => {
  return useFeatureIsOn(quiteModeKey);
};
export const useQuietModeFeatureIsOn = (): boolean =>
  isGrowthbookFeatureOn(quiteModeKey);

// Ai image gen - Ideogram model
export const aiImageGenIdeogramModelKey = 'ideogram';

export const useIsAiImageGenIdeogramModelEnabled = (): boolean => {
  return useFeatureIsOn(aiImageGenIdeogramModelKey);
};

const aiImageGenStickyFooterKey = 'image-gen-sticky-footer';

// We want to test if making the image gen footer sticky affects image gen usage.
export const useIsAiImageGenStickyFooterEnabled = (): boolean => {
  return useFeatureIsOn(aiImageGenStickyFooterKey);
};

const aiImageGenHiddenCredits = 'image-gen-hidden-credits';

// We want to test whether hiding credit cost improves image gen usage.
export const useIsAiImageGenHiddenCreditsEnabled = (): boolean => {
  return useFeatureIsOn(aiImageGenHiddenCredits);
};

export const useCopyPasteStyleEnabled = (): boolean =>
  useFeatureIsOn('editor-copy-paste-style');

export const useIsNewFontPanelEnabled = (): boolean => {
  return useFeatureIsOn('new-font-panel');
};

export const useIsDiscountReferralRewardEnabled = (): boolean => {
  return useFeatureIsOn('discount-referral-reward');
};

// shows an upgrade button in the bottom menu of the editor
// cleanup task: https://app.clickup.com/t/86c21gh2v
export const useEditorUpgradeCTA = (): boolean => {
  return useFeatureIsOn('editor-upgrade-cta');
};

// shows two plans (pro and expert) when user is on free plan instead of one (pro plan)
// cleanup task: https://app.clickup.com/t/86c28zbe3
export const useShowTwoPlansForFreeUser = (): boolean => {
  return useFeatureIsOn('two-plans-for-free-upgrade');
};

export const newElementsPanel = 'new-elements-panel';
export const newElementsPanelIsOn = (): boolean =>
  isGrowthbookFeatureOn(newElementsPanel);
export const useNewElementsPanel = (): boolean => {
  return useFeatureIsOn(newElementsPanel);
};

export const useIsOutpaintingFeatureEnabled = (): boolean => {
  return useFeatureIsOn('outpainting');
};

export const MOBILE_DRAWER_ADD_SECTION_UPDATES =
  'mobile-drawer-add-section-updates';

export const isMobileDrawerAddSectionUpdatesEnabled = (): boolean =>
  isGrowthbookFeatureOn(MOBILE_DRAWER_ADD_SECTION_UPDATES);

export const useMobileDrawerAddSectionUpdates = (): boolean => {
  return useFeatureIsOn(MOBILE_DRAWER_ADD_SECTION_UPDATES);
};

export const useDashboardTileImprovements = (): boolean => {
  return useFeatureIsOn('dashboard-tile-improvements');
};
export const useHighlightProPlan = (): boolean => {
  return useFeatureIsOn('highlight-pro-plan');
};

/**
 * Collaboration Feature Flags start
 **/
const editorCommentReactionKey = 'editor-comment-reaction';
export const useEditorCommentReactionFeature = (): boolean => {
  return useFeatureIsOn(editorCommentReactionKey);
};

const collabNotificationsKey = 'collab-invites-as-notifications';

export const getCollabInvitesAsNotificationsEnabled = (): boolean => {
  return isGrowthbookFeatureOn(collabNotificationsKey);
};

export const useCollabInvitesAsNotificationsEnabled = (): boolean => {
  return useFeatureIsOn(collabNotificationsKey);
};

const collabNewShareModalKey = 'collab-new-share-modal';
export const useCollabNewShareModal = (): boolean =>
  useFeatureIsOn(collabNewShareModalKey);

export const useWorkspacesFeatureEnabled = (): boolean =>
  useFeatureIsOn('workspaces');

const collabInviteToast = 'collab-invite-toast';
export const useCollabInviteToast = (): boolean =>
  useFeatureIsOn(collabInviteToast);
/**
 * Collaboration Feature Fags end
 */

const aiChatKey = 'ai-chat';
export const useAiChatFeature = (): boolean => {
  return useFeatureIsOn(aiChatKey);
};

export const useAddBusinessPlan = (): boolean =>
  useFeatureIsOn('add-business-plan');

export const isFreeAdvancedImageGenerationEnabled = (): boolean => {
  return isGrowthbookFeatureOn('free-advanced-image-generation');
};

export const useFreeAdvancedImageGenerationEnabled = (): boolean => {
  return useFeatureIsOn('free-advanced-image-generation');
};
